import { useEffect } from 'react';

interface SeoHeadProps {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl: string;
  ogType?: string;
  ogImage?: string;
  jsonLd?: Record<string, unknown>;
}

export default function SeoHead({
  title,
  description,
  keywords,
  canonicalUrl,
  ogType = 'website',
  ogImage,
  jsonLd,
}: SeoHeadProps) {
  const fullTitle = `${title} - 就去色色`;
  const fullUrl = `https://jiuqu.sese${canonicalUrl}`;

  useEffect(() => {
    const originalTitle = document.title;
    document.title = fullTitle;

    const metaTags: HTMLMetaElement[] = [];

    function setMeta(name: string, content: string, isProperty = false) {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
        metaTags.push(el);
      }
      el.setAttribute('content', content);
    }

    setMeta('description', description);
    setMeta('keywords', keywords);

    const canonicalEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (canonicalEl) {
      canonicalEl.setAttribute('href', fullUrl);
    } else {
      const link = document.createElement('link');
      link.rel = 'canonical';
      link.href = fullUrl;
      document.head.appendChild(link);
    }

    setMeta('og:title', fullTitle, true);
    setMeta('og:description', description, true);
    setMeta('og:url', fullUrl, true);
    setMeta('og:type', ogType, true);
    setMeta('og:site_name', '就去色色', true);
    setMeta('og:locale', 'zh_CN', true);
    if (ogImage) setMeta('og:image', ogImage, true);

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', fullTitle);
    setMeta('twitter:description', description);
    if (ogImage) setMeta('twitter:image', ogImage);

    let jsonLdScript: HTMLScriptElement | null = null;
    if (jsonLd) {
      jsonLdScript = document.createElement('script');
      jsonLdScript.type = 'application/ld+json';
      jsonLdScript.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(jsonLdScript);
    }

    return () => {
      document.title = originalTitle;
      metaTags.forEach((el) => el.remove());
      if (jsonLdScript) jsonLdScript.remove();
    };
  }, [fullTitle, fullUrl, description, keywords, ogType, ogImage, jsonLd]);

  return null;
}